import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Plus, Trash2 } from "lucide-react";
import { api, type LocalesResponse } from "../api";
import { useToast } from "../components/Toast";
import ConfirmDialog from "../components/ConfirmDialog";
import type { Entry, LocaleCode } from "../../shared/types";

export default function Locales() {
  const [cfg, setCfg] = useState<LocalesResponse | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [removing, setRemoving] = useState<LocaleCode | null>(null);
  const toast = useToast();

  async function reload() {
    const [c, e] = await Promise.all([api.getLocales(), api.listEntries()]);
    setCfg(c);
    setEntries(e);
  }

  useEffect(() => {
    reload();
  }, []);

  const stats = useMemo(() => {
    if (!cfg) return new Map<LocaleCode, { full: number; total: number }>();
    const m = new Map<LocaleCode, { full: number; total: number }>();
    for (const lang of cfg.locales) {
      let full = 0;
      for (const e of entries) {
        const t = e.titles[lang]?.trim() ?? "";
        const c = e.capitals[lang]?.trim() ?? "";
        if (t && c) full++;
      }
      m.set(lang, { full, total: entries.length });
    }
    return m;
  }, [cfg, entries]);

  async function handleAdd() {
    if (!/^[a-z]{2}$/.test(newCode)) {
      toast.show("error", "Code must be exactly 2 lowercase letters");
      return;
    }
    try {
      await api.addLocale(newCode);
      toast.show("success", `Locale ${newCode} added`);
      setShowAdd(false);
      setNewCode("");
      await reload();
    } catch (err) {
      toast.show("error", (err as Error).message);
    }
  }

  async function handleRemove() {
    if (!removing) return;
    try {
      await api.removeLocale(removing);
      toast.show("success", `Locale ${removing} removed`);
      setRemoving(null);
      await reload();
    } catch (err) {
      toast.show("error", (err as Error).message);
    }
  }

  if (!cfg) return <div className="text-gh-muted">Loading…</div>;

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Locales</h2>
        <button
          onClick={() => setShowAdd(true)}
          className="px-3 py-1.5 rounded bg-gh-success text-white text-sm flex items-center gap-1 hover:opacity-90"
        >
          <Plus className="w-4 h-4" /> Add locale
        </button>
      </div>

      <div className="bg-gh-surface border border-gh-border rounded">
        {cfg.locales.map((lang) => {
          const s = stats.get(lang)!;
          const isDefault = lang === cfg.default;
          return (
            <div
              key={lang}
              className="flex items-center px-4 py-2 border-b border-gh-border last:border-b-0 text-sm"
            >
              <span className="font-mono text-xs uppercase w-20">{lang}</span>
              <span className="text-gh-muted text-xs flex-1">
                {isDefault ? "(default)" : ""}
              </span>
              <span className="text-xs text-gh-muted mr-4">
                {s.full}/{s.total} fully translated
              </span>
              <a
                href={`${cfg.publicOrigin}/countries_${lang}.json`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gh-muted hover:text-gh-accent mr-2"
                aria-label={`open countries_${lang}.json`}
                title={`Open countries_${lang}.json`}
              >
                <ExternalLink className="w-4 h-4" />
              </a>
              {!isDefault && (
                <button
                  onClick={() => setRemoving(lang)}
                  className="text-gh-muted hover:text-gh-danger"
                  aria-label={`remove ${lang}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30">
          <div className="bg-gh-surface rounded-lg shadow-lg max-w-sm w-full p-6 border border-gh-border">
            <h3 className="text-lg font-semibold mb-3">Add locale</h3>
            <input
              type="text"
              placeholder="2-letter ISO-639-1 (e.g. ko)"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toLowerCase())}
              className="w-full px-3 py-1.5 rounded border border-gh-border text-sm mb-2"
              maxLength={2}
            />
            <p className="text-xs text-gh-muted mb-3">
              See the full list on{" "}
              <a
                href="https://en.wikipedia.org/wiki/List_of_ISO_639_language_codes"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gh-accent hover:underline"
              >
                Wikipedia: ISO 639-1 codes
              </a>
              .
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAdd(false)}
                className="px-4 py-1.5 rounded border border-gh-border text-sm hover:bg-gh-bg"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                className="px-4 py-1.5 rounded bg-gh-accent text-white text-sm hover:opacity-90"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={removing !== null}
        title={`Remove locale ${removing}?`}
        body={
          <>
            Removes <code>countries_{removing}.json</code> from the public origin.
            All <code>{removing}</code> translations are stripped from the canonical store.
            Snapshots saved in <code>_cms/backups/</code>.
          </>
        }
        confirmLabel="Remove"
        destructive
        onConfirm={handleRemove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
